import { createDrawer, Drawer } from '@nexelem/drawer';
import { Banner, IconButton, Popover, Status } from '@nexelem/components';
import { useState } from 'react';
import { TaskStatus } from '@nexelem/graphne-api';
import { GanttUtils } from '../utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFlag,
  faClock,
  faMessageLines,
  faPauseCircle,
  faSquareXmark,
  faEmptySet,
  faSnowflake,
} from '@fortawesome/pro-solid-svg-icons';
import { faCube, faCircleExclamationCheck } from '@fortawesome/pro-regular-svg-icons';
import { faCirclesOverlap, faCircleQuestion } from '@fortawesome/pro-light-svg-icons';
import { useCustomTranslation } from '../../../../hooks';

const LEGEND_STATUSES = [TaskStatus.Pending, TaskStatus.InProgress, TaskStatus.Completed, TaskStatus.Error];

type GanttLegendDrawerProps = {
  isSimulation: boolean;
};

const GanttLegendDrawer = ({ isSimulation }: GanttLegendDrawerProps) => {
  const [isOpen, setIsOpen] = useState(true);
  const getStatusTranslation = GanttUtils.useTaskStatusTranslation();
  const { tWrapper } = useCustomTranslation('products.productionPlan.gantt.legend');

  return (
    <Drawer>
      <Drawer.Header title={<div className="text-t24-sb">{tWrapper('title')}</div>} onClose={closeGanttLegendDrawer} />
      <Drawer.Content>
        {isOpen && !isSimulation && (
          <Banner
            withCloseButton
            className="mb-4"
            message={tWrapper('bannerMessage')}
            onClose={() => setIsOpen(false)}
          />
        )}
        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-3">
            <span className="text-t16-b">{tWrapper('statuses')}</span>
            <div className="flex flex-col gap-3 pl-1">
              {LEGEND_STATUSES.map((status) => (
                <div className="flex items-center gap-1" key={status}>
                  <Status className="mx-1" color={GanttUtils.getTaskStatusColor(status)} variant="dot" />
                  <span className="text-s18">{getStatusTranslation(status)}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <span className="text-t16-b">{tWrapper('objectsOnPlan')}</span>
            <div className="flex flex-col gap-1 pl-1">
              <div className="flex items-center gap-2">
                <div className="h-4 w-6 rounded-md border border-border-primary bg-surface-container"></div>
                <span className="py-2.5 text-s18">{tWrapper('batch')}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex h-4 w-6 items-center justify-center rounded-md border border-border-primary bg-surface-container">
                  <FontAwesomeIcon className="w-4 text-icon-darker" icon={faCirclesOverlap} />
                </div>
                <span className="py-2.5 text-s18">{tWrapper('series')}</span>
                <Popover.Root>
                  <Popover.Trigger>
                    <IconButton Icon={<FontAwesomeIcon icon={faCircleQuestion} />} size="small" />
                  </Popover.Trigger>
                  <Popover.Content>{tWrapper('seriesDescription')}</Popover.Content>
                </Popover.Root>
              </div>
              <div className="flex items-center gap-2">
                <div className="relative h-4 w-6">
                  <div className="absolute -top-0.75 left-0.75 h-3.25 w-5.25 rounded-md border border-border-primary-darker bg-surface-container" />
                  <div className="absolute top-0 left-0 flex h-3.25 w-5.25 items-center justify-center rounded-md border border-border-primary-darker bg-surface-container">
                    <span className="text-s10-sb">2</span>
                  </div>
                </div>
                <span className="py-2.5 text-s18">{tWrapper('groupOfBatches')}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="bg-surface-retooling-legend flex h-4 w-6 overflow-hidden rounded-l-xs rounded-r-md border border-border-primary bg-surface-container"></div>
                <span className="py-2.5 text-s18">{tWrapper('retooling')}</span>
                <Popover.Root>
                  <Popover.Trigger>
                    <IconButton Icon={<FontAwesomeIcon icon={faCircleQuestion} />} size="small" />
                  </Popover.Trigger>
                  <Popover.Content>{tWrapper('retoolingDescription')}</Popover.Content>
                </Popover.Root>
              </div>
              <div className="flex items-center gap-2">
                <div className="bg-surface-break-legend flex h-4 w-6 rounded-xs border border-border-primary-darker bg-surface-container"></div>
                <span className="py-2.5 text-s18">{tWrapper('plannedBreak')}</span>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <span className="text-t16-b">{tWrapper('specialMarkings')}</span>
            <div className="flex flex-col gap-1 pl-1">
              {isSimulation && (
                <>
                  <div className="flex items-center gap-2">
                    <FontAwesomeIcon className="w-4.5 text-icon-darker" icon={faEmptySet} />
                    <span className="py-2.5 text-s18">{tWrapper('unpublished')}</span>
                    <Popover.Root>
                      <Popover.Trigger>
                        <IconButton Icon={<FontAwesomeIcon icon={faCircleQuestion} />} size="small" />
                      </Popover.Trigger>
                      <Popover.Content>{tWrapper('unpublishedDescription')}</Popover.Content>
                    </Popover.Root>
                  </div>
                  <div className="flex items-center gap-2">
                    <FontAwesomeIcon className="w-4.5 text-icon-focus-dark" icon={faSnowflake} />
                    <span className="py-2.5 text-s18">{tWrapper('frozen')}</span>
                    <Popover.Root>
                      <Popover.Trigger>
                        <IconButton Icon={<FontAwesomeIcon icon={faCircleQuestion} />} size="small" />
                      </Popover.Trigger>
                      <Popover.Content>{tWrapper('frozenDescription')}</Popover.Content>
                    </Popover.Root>
                  </div>
                </>
              )}
              <div className="flex items-center gap-2">
                <FontAwesomeIcon className="w-4.5 text-icon-brand" icon={faFlag} />
                <span className="py-2.5 text-s18">{tWrapper('priority')}</span>
              </div>
              {!isSimulation && (
                <div className="flex items-center gap-2">
                  <FontAwesomeIcon className="w-4.5 text-icon-status-orange-lighter" icon={faCircleExclamationCheck} />
                  <span className="py-2.5 text-s18">{tWrapper('toAccept')}</span>
                  <Popover.Root>
                    <Popover.Trigger>
                      <IconButton Icon={<FontAwesomeIcon icon={faCircleQuestion} />} size="small" />
                    </Popover.Trigger>
                    <Popover.Content>{tWrapper('toAcceptDescription')}</Popover.Content>
                  </Popover.Root>
                </div>
              )}
              <div className="flex items-center gap-2">
                <FontAwesomeIcon className="w-4.5 text-icon-status-yellow" icon={faCube} />
                <span className="py-2.5 text-s18">{tWrapper('stockShortage')}</span>
              </div>
              <div className="flex items-center gap-2">
                <FontAwesomeIcon className="w-4.5 text-surface-status-rose-darker" icon={faClock} />
                <span className="py-2.5 text-s18">{tWrapper('overdue')}</span>
              </div>
              <div className="flex items-center gap-2">
                <FontAwesomeIcon className="w-4.5 text-icon-darker" icon={faMessageLines} />
                <span className="py-2.5 text-s18">{tWrapper('comment')}</span>
              </div>
              <div className="flex items-center gap-2">
                <FontAwesomeIcon className="w-4.5 text-icon-darker" icon={faPauseCircle} />
                <span className="py-2.5 text-s18">{tWrapper('paused')}</span>
              </div>
              <div className="flex items-center gap-2">
                <FontAwesomeIcon className="w-4.5 text-icon-darker" icon={faSquareXmark} />
                <span className="py-2.5 text-s18">{tWrapper('inactiveWorkstation')}</span>
              </div>
            </div>
          </div>
        </div>
      </Drawer.Content>
    </Drawer>
  );
};

export const { toggleGanttLegendDrawer, closeGanttLegendDrawer } = createDrawer(GanttLegendDrawer, {
  name: 'GanttLegendDrawer',
  width: 420,
  slot: 'left',
  drawerId: () => 'gantt-legend-drawer',
});
